import api from "./api";
import { resolveRoleName } from "../utils/auth";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

const extractTaskArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const toQuery = ({ page, limit, search } = {}) => {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim()) {
    query.append("search", String(search).trim());
  }
  return query.toString();
};

const extractMeta = (payload, page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
  if (payload?.meta && typeof payload.meta === "object") {
    return {
      total: Number(payload.meta.total) || 0,
      page: Number(payload.meta.page) || Number(page) || DEFAULT_PAGE,
      limit: Number(payload.meta.limit) || Number(limit) || DEFAULT_LIMIT,
      totalPages: Number(payload.meta.totalPages) || 1,
    };
  }
  const data = extractTaskArray(payload);
  return {
    total: data.length,
    page: Number(page) || DEFAULT_PAGE,
    limit: Number(limit) || DEFAULT_LIMIT,
    totalPages: 1,
  };
};

export async function getTasks() {
  const res = await api.get("/task");
  return extractTaskArray(res).map(normalizeTask);
}

export async function getTasksPaginated({
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
  search = "",
} = {}) {
  const qs = toQuery({ page, limit, search });
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/task" : (
      "/hierarchical-view/relation/task/bulk"
    );

  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  return {
    data: extractTaskArray(res).map(normalizeTask),
    meta: extractMeta(res, page, limit),
  };
}

export async function getTasksByCompanyId(companyId) {
  if (!companyId) throw new Error("Company ID is required");
  try {
    const res = await api.get(`/task/company/${encodeURIComponent(companyId)}`);
    const arr =
      Array.isArray(res) ? res
      : Array.isArray(res?.data) ? res.data
      : [];
    return arr.map(normalizeTask);
  } catch {
    const fallback = await api.get(
      `/task?companyId=${encodeURIComponent(companyId)}`,
    );
    const arr =
      Array.isArray(fallback) ? fallback
      : Array.isArray(fallback?.data) ? fallback.data
      : [];
    return arr.map(normalizeTask);
  }
}

export async function getTaskById(id) {
  if (id == null) throw new Error("Task id is required");
  return api.get(`/task/${id}`);
}

export async function getTaskWithRelations(id) {
  if (id == null) throw new Error("Task id is required");
  try {
    // Preferred singular pattern
    const res = await api.get(`/task/${id}/with-relations`);
    return normalizeTaskRelations(res || null);
  } catch (e) {
    // Optional fallback if backend also supports a plural style
    try {
      const res2 = await api.get(`/task/${id}/with-relations`);
      const one = Array.isArray(res2) ? res2[0] || null : res2 || null;
      return normalizeTaskRelations(one);
    } catch (err) {
      throw err;
    }
  }
}

// Normalize backend variations to a consistent shape for the UI
function normalizeTaskRelations(dto) {
  if (!dto || typeof dto !== "object") return dto;
  const out = { ...dto };
  // Ensure taskSkills exists; map from task_skill if needed
  if (!Array.isArray(out.taskSkills) && Array.isArray(out.task_skill)) {
    out.taskSkills = out.task_skill.map((ts) => ({
      ...ts,
      // Ensure 'skill' object is present for UI access
      skill:
        ts?.skill ||
        (ts?.skill_name ?
          { name: ts.skill_name, description: null }
        : undefined),
      // Map 'skill_level' -> 'level' for UI access
      level: ts?.level || ts?.skill_level || undefined,
    }));
  }
  return out;
}

// Backend's ValidationPipe is strict (whitelist + forbidNonWhitelisted).
// Omit the key entirely (not []) when there's nothing to assign.
// time_allocation_percentage is optional — only include when provided.
function buildRaciPayload(raci) {
  if (!Array.isArray(raci) || raci.length === 0) return undefined;
  return raci.map((r) => {
    const entry = {
      job_id: Number(r.job_id),
      role: r.role ?? null,
    };
    if (r.time_allocation_percentage != null) {
      entry.time_allocation_percentage = Number(r.time_allocation_percentage);
    }
    return entry;
  });
}

export async function createTask(payload) {
  const body = {
    task_name: payload.task_name ?? payload.taskName ?? payload.name ?? "",
    task_code: payload.task_code ?? payload.taskCode ?? payload.code ?? "",
    task_version:
      payload.task_version != null ? Number(payload.task_version)
      : payload.version != null ? Number(payload.version)
      : payload.task_version === null ? null
      : undefined,
    task_company_id:
      payload.task_company_id ??
      payload.company_id ??
      payload.companyId ??
      null,
    expected_process_time:
      payload.expected_process_time != null ? Number(payload.expected_process_time) : null,
    expected_waiting_time:
      payload.expected_waiting_time != null ? Number(payload.expected_waiting_time) : null,
    expected_rework_time:
      payload.expected_rework_time != null ? Number(payload.expected_rework_time) : null,
    status_id:
      payload.status_id != null ? Number(payload.status_id)
      : payload.status_id === null ? null
      : undefined,
    category_id:
      payload.category_id != null ? Number(payload.category_id)
      : payload.category_id === null ? null
      : undefined,
    task_process_id:
      payload.task_process_id ??
      payload.process_id ??
      payload.processId ??
      null,
    task_overview: payload.task_overview ?? payload.overview ?? "",
    frequency_period: payload.frequency_period || "WEEK",
    frequency_interval:
      payload.frequency_interval != null ?
        Number(payload.frequency_interval)
      : 1,
    occurrences:
      payload.occurrences != null ? Number(payload.occurrences) : 1,
    // Deprecated by the backend in favor of `raci`; only used as a fallback
    // when no RACI assignment has been made yet (see `raci` below).
    job_ids: Array.isArray(payload.job_ids) ? payload.job_ids : undefined,
    // Sending `raci: []` fails backend validation (zero Accountable), so the
    // key must be omitted entirely rather than sent empty.
    raci: buildRaciPayload(payload.raci),
    // Optional: pass through taskSkills if backend expects this key
    taskSkills:
      Array.isArray(payload.taskSkills) ? payload.taskSkills : undefined,
  };
  // Only include 'skills' when 'taskSkills' is not provided
  if (!Array.isArray(body.taskSkills)) {
    body.skills =
      Array.isArray(payload.skills) ? payload.skills
      : Array.isArray(payload.taskSkills) ?
        payload.taskSkills.map((s) => ({
          name: s.skill_name ?? s.name,
          level: s.level,
        }))
      : undefined;
  }
  return api.post("/task", body);
}

export async function updateTask(id, payload) {
  if (id == null) throw new Error("Task id is required");
  const body = {
    task_name: payload.task_name ?? payload.taskName ?? payload.name,
    task_code: payload.task_code ?? payload.taskCode ?? payload.code,
    task_version:
      payload.task_version != null ? Number(payload.task_version)
      : payload.version != null ? Number(payload.version)
      : payload.task_version === null ? null
      : undefined,
    task_company_id:
      payload.task_company_id ?? payload.company_id ?? payload.companyId,
    expected_process_time:
      payload.expected_process_time != null ? Number(payload.expected_process_time) : null,
    // optimistic_process_time / pessimistic_process_time are not part of the
    // backend's task schema ("property ... should not exist"); omit them
    // entirely rather than sending null, matching createTask()'s payload.
    expected_waiting_time:
      payload.expected_waiting_time != null ? Number(payload.expected_waiting_time) : null,
    expected_rework_time:
      payload.expected_rework_time != null ? Number(payload.expected_rework_time) : null,
    status_id:
      payload.status_id != null ? Number(payload.status_id)
      : payload.status_id === null ? null
      : undefined,
    category_id:
      payload.category_id != null ? Number(payload.category_id)
      : payload.category_id === null ? null
      : undefined,
    task_process_id:
      payload.task_process_id ?? payload.process_id ?? payload.processId,
    task_overview: payload.task_overview ?? payload.overview,
    frequency_period: payload.frequency_period || "WEEK",
    frequency_interval:
      payload.frequency_interval != null ?
        Number(payload.frequency_interval)
      : 1,
    occurrences:
      payload.occurrences != null ? Number(payload.occurrences) : 1,
    // Deprecated by the backend in favor of `raci`; only used as a fallback
    // when no RACI assignment has been made yet (see `raci` below).
    job_ids: Array.isArray(payload.job_ids) ? payload.job_ids : undefined,
    // Sending `raci: []` fails backend validation (zero Accountable), so the
    // key must be omitted entirely rather than sent empty.
    raci: buildRaciPayload(payload.raci),
    // Optional passthrough for backends that expect taskSkills key
    taskSkills:
      Array.isArray(payload.taskSkills) ? payload.taskSkills : undefined,
  };
  // Only include 'skills' when 'taskSkills' is not provided
  if (!Array.isArray(body.taskSkills)) {
    body.skills =
      Array.isArray(payload.skills) ? payload.skills
      : Array.isArray(payload.taskSkills) ?
        payload.taskSkills.map((s) => ({
          name: s.skill_name ?? s.name,
          level: s.level,
        }))
      : undefined;
  }
  return api.patch(`/task/${id}`, body);
}

export async function deleteTask(id) {
  if (id == null) throw new Error("Task id is required");
  return api.delete(`/task/${id}`);
}

// Associate a job to a task
export async function addJobToTask(taskId, jobId) {
  if (taskId == null || jobId == null)
    throw new Error("Task ID and Job ID are required");
  // Prefer a dedicated association endpoint if available
  try {
    return await api.post(`/task/${taskId}/jobs`, { job_id: Number(jobId) });
  } catch (_) {
    // Fallback to a generic mapping endpoint
    return await api.post(`/task-job`, {
      task_id: Number(taskId),
      job_id: Number(jobId),
    });
  }
}

// Remove a job association from a task
export async function removeJobFromTask(taskId, jobId) {
  if (taskId == null || jobId == null)
    throw new Error("Task ID and Job ID are required");
  try {
    return await api.delete(`/task/${taskId}/jobs/${jobId}`);
  } catch (_) {
    // Fallback using query params if REST style not available
    return await api.delete(
      `/task-job?task_id=${encodeURIComponent(
        taskId,
      )}&job_id=${encodeURIComponent(jobId)}`,
    );
  }
}

// Normalize simple task shape for bulk and list usage
// (hoisted above getTasks which uses it)
function normalizeTask(dto) {
  const normalized = {
    ...dto,
    task_id: dto?.task_id ?? dto?.id,
    task_name: dto?.task_name ?? dto?.name ?? "",
    task_code: dto?.task_code ?? dto?.code ?? "",
    task_company_id:
      dto?.task_company_id ??
      dto?.company_id ??
      dto?.company?.company_id ??
      null,
    expected_process_time: dto?.expected_process_time ?? null,
    expected_waiting_time: dto?.expected_waiting_time ?? null,
    expected_rework_time: dto?.expected_rework_time ?? null,
    task_overview: dto?.task_overview ?? dto?.overview ?? "",
    frequency_period: dto?.frequency_period ?? "WEEK",
    frequency_interval: dto?.frequency_interval ?? 1,
    occurrences: dto?.occurrences ?? 1,
    task_process_id: dto?.task_process_id ?? dto?.process_id ?? null,
    category_id:
      dto?.category_id ??
      dto?.categoryId ??
      dto?.task_category_id ??
      dto?.taskCategoryId ??
      null,
    status_id:
      dto?.status_id ??
      dto?.statusId ??
      dto?.task_status_id ??
      dto?.taskStatusId ??
      null,
    created_at: dto?.created_at ?? dto?.createdAt ?? null,
    updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
  };
  return normalized;
}

// In-flight request coalescing for bulk tasks
const _pendingTasksBulk = new Map(); // key -> Promise

// POST /hierarchical-view/relation/task/bulk
// Body: { userIds: number[] }
// Returns de-duplicated normalized list of tasks from associatedTasks + createdTasks
export async function getTasksByUsersBulk(userIds = []) {
  // SUPER_ADMIN should use full list (keep behavior unchanged)
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    const currentUser = auth?.user;
    if (currentUser) {
      const rn = await resolveRoleName(currentUser);
      const norm = String(rn || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
      if (norm === "SUPERADMIN") {
        const res = await getTasks();
        return Array.isArray(res) ? res.map(normalizeTask) : [];
      }
    }
  } catch {}
  const ids =
    Array.isArray(userIds) ?
      userIds.map((n) => Number(n)).filter((n) => Number.isInteger(n))
    : [];
  if (ids.length === 0) return [];
  const key = ids
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  if (_pendingTasksBulk.has(key)) return _pendingTasksBulk.get(key);
  const payload = { userIds: ids };
  const inflight = (async () => {
    const res = await api.post(
      "/hierarchical-view/relation/task/bulk",
      payload,
    );
    const results =
      Array.isArray(res?.results) ? res.results
      : Array.isArray(res) ? res
      : [];
    const coll = [];
    for (const r of results) {
      const data = r?.data || {};
      const assoc =
        Array.isArray(data.associatedTasks) ? data.associatedTasks : [];
      const created = Array.isArray(data.createdTasks) ? data.createdTasks : [];
      coll.push(...assoc, ...created);
    }
    const byId = new Map();
    coll.forEach((t) => {
      const nt = normalizeTask(t);
      const k = String(nt.task_id ?? "");
      if (!byId.has(k)) byId.set(k, nt);
    });
    return Array.from(byId.values());
  })().finally(() => {
    _pendingTasksBulk.delete(key);
  });
  _pendingTasksBulk.set(key, inflight);
  return inflight;
}
